import React, { useState } from 'react';
import {
  Box,
  Container,
  VStack,
  HStack,
  Button,
  Card,
  CardBody,
  Text,
  Heading,
  Icon,
  Progress,
  Flex,
  Badge
} from '@chakra-ui/react';
import { ArrowBackIcon, ArrowForwardIcon, CheckIcon, AddIcon, StarIcon } from '@chakra-ui/icons';

const Tutorial = ({ onRouteChange }) => {
  const [currentStep, setCurrentStep] = useState(0);

  const tutorialSteps = [
    {
      title: "Welcome to Grow! 👋",
      content: "Grow helps you create personalized goals and practice them with AI-generated exercises. Let's take a quick tour!",
      action: "Get Started",
      highlight: null
    },
    {
      title: "Create Your First Goal",
      content: "Click the 'Create New Goal' button to set up your personal development journey. You'll describe what you want to work on, and our AI will create progressive exercises for you.",
      action: "Show me where",
      highlight: "create-goal",
      buttonText: "Create New Goal",
      buttonIcon: AddIcon,
      buttonColor: "purple",
      buttonVariant: "outline"
    },
    {
      title: "Practice Daily",
      content: "Once you have goals, you'll see 'Today's Practice' on your dashboard. This shows your next exercise to complete. Consistent daily practice leads to real growth!",
      action: "Got it",
      highlight: "daily-practice",
      feature: "Today's Practice card"
    },
    {
      title: "Random Practice",
      content: "Feeling spontaneous? Use 'Start Random Practice' to work on any incomplete exercise from your goals. Great for mixing up your routine!",
      action: "Sounds good",
      highlight: "random-practice",
      buttonText: "Start Random Practice",
      buttonIcon: StarIcon,
      buttonColor: "orange",
      buttonVariant: "ghost"
    },
    {
      title: "Track Your Progress",
      content: "Your dashboard shows your active goals, weekly progress, and current streak. Watch your consistency grow over time!",
      action: "Nice!",
      highlight: "progress-stats",
      feature: "Progress overview cards"
    },
    {
      title: "You're All Set! 🎉",
      content: "That's the basics! Start by creating your first goal, then practice a little each day. Remember: small, consistent steps lead to big changes.",
      action: "Create My First Goal",
      highlight: null,
      final: true
    }
  ];

  const currentTutorialStep = tutorialSteps[currentStep];

  const nextStep = () => {
    if (currentStep < tutorialSteps.length - 1) {
      setCurrentStep(currentStep + 1);
    }
  };

  const prevStep = () => {
    if (currentStep > 0) {
      setCurrentStep(currentStep - 1);
    }
  };

  const handleFinish = () => {
    if (currentTutorialStep.final) {
      onRouteChange('createGoal');
    } else {
      nextStep();
    }
  };

  const skipTutorial = () => {
    onRouteChange('dashboard');
  };

  return (
    <Box minH="100vh" bg="gray.50">
      <Container maxW="4xl" py={{ base: 6, md: 12 }}>
        <VStack spacing={8} align="stretch">
          {/* Header */}
          <Box textAlign="center">
            <Heading size="xl" color="purple.700" mb={2}>
              Getting Started Tutorial
            </Heading>
            <Text color="gray.600" fontSize="lg">
              Learn how to use Grow in just a few minutes
            </Text>
          </Box>

          {/* Progress */}
          <Box>
            <Flex justify="space-between" align="center" mb={2}>
              <Text fontSize="sm" color="gray.600">
                Step {currentStep + 1} of {tutorialSteps.length}
              </Text>
              <Badge colorScheme="purple" variant="subtle">
                {Math.round(((currentStep + 1) / tutorialSteps.length) * 100)}% Complete
              </Badge>
            </Flex>
            <Progress
              value={((currentStep + 1) / tutorialSteps.length) * 100}
              colorScheme="purple"
              bg="gray.200"
              borderRadius="full"
              size="sm"
            />
          </Box>

          {/* Tutorial Card */}
          <Card bg="white" shadow="xl" borderTop="4px" borderColor="purple.500">
            <CardBody p={{ base: 6, md: 8 }}>
              <VStack spacing={6} align="stretch">
                <Box textAlign="center">
                  <Heading size="lg" color="gray.800" mb={4}>
                    {currentTutorialStep.title}
                  </Heading>
                  <Text fontSize="lg" color="gray.600" lineHeight="tall" maxW="600px" mx="auto">
                    {currentTutorialStep.content}
                  </Text>
                </Box>

                {/* Feature Highlight */}
                {currentTutorialStep.highlight && (
                  <Box bg="purple.50" borderRadius="lg" p={6} border="1px" borderColor="purple.200" minH="120px" display="flex" alignItems="center" justifyContent="center">
                    <VStack spacing={4} w="full">
                      {currentTutorialStep.buttonText && (
                        <Button
                          leftIcon={<Icon as={currentTutorialStep.buttonIcon} />}
                          colorScheme={currentTutorialStep.buttonColor}
                          variant={currentTutorialStep.buttonVariant || "solid"}
                          size="lg"
                          isDisabled
                          opacity={0.8}
                          px={8}
                          py={3}
                          fontSize="md"
                          fontWeight="600"
                        >
                          {currentTutorialStep.buttonText}
                        </Button>
                      )}
                      {currentTutorialStep.feature && (
                        <Text fontSize="sm" color="purple.700" fontWeight="medium" textAlign="center">
                          Look for: {currentTutorialStep.feature}
                        </Text>
                      )}
                    </VStack>
                  </Box>
                )}

                {/* Navigation */}
                <HStack justify="space-between" pt={4}>
                  <Button
                    variant="ghost"
                    colorScheme="gray"
                    onClick={skipTutorial}
                  >
                    Skip Tutorial
                  </Button>
                  
                  <HStack spacing={3}>
                    {currentStep > 0 && (
                      <Button
                        variant="outline"
                        colorScheme="purple"
                        leftIcon={<ArrowBackIcon />}
                        onClick={prevStep}
                      >
                        Back
                      </Button>
                    )}
                    
                    <Button
                      colorScheme="purple"
                      rightIcon={currentTutorialStep.final ? <CheckIcon /> : <ArrowForwardIcon />}
                      onClick={handleFinish}
                      size="lg"
                    >
                      {currentTutorialStep.action}
                    </Button>
                  </HStack>
                </HStack>
              </VStack>
            </CardBody>
          </Card>

          {/* Quick Tips */}
          <Card bg="purple.50" borderLeft="4px" borderColor="purple.500">
            <CardBody p={4}>
              <HStack spacing={3}>
                <Icon as={StarIcon} color="purple.500" />
                <VStack align="start" spacing={1}>
                  <Text fontSize="sm" fontWeight="bold" color="purple.700">
                    Pro Tip
                  </Text>
                  <Text fontSize="sm" color="purple.600">
                    The key to success is consistency, not perfection. Even 5 minutes a day makes a difference!
                  </Text>
                </VStack>
              </HStack>
            </CardBody>
          </Card>
        </VStack>
      </Container>
    </Box>
  );
};

export default Tutorial;